// Event maps are large site plans, so unlike imageConverter.js (512px avatars)
// this keeps as much detail as browsers can comfortably render while stopping
// multi-hundred-MB originals reaching S3. The byte cap here must match the
// server's cap in the event-map upload-url route, which signs the exact
// content length into the presigned PUT.
export const EVENT_MAP_MAX_UPLOAD_BYTES = 25 * 1024 * 1024

// Beyond this, decoding the image is likely to exhaust tab memory before we
// ever get a chance to downscale it.
const MAX_SOURCE_BYTES = 250 * 1024 * 1024

// Long-edge and total-pixel ceilings for the decoded bitmap the CAD screen
// has to keep in memory (40MP ≈ 160MB decoded RGBA).
const MAX_EDGE = 8192
const MAX_PIXELS = 40e6

// WebP first — roughly half the bytes of JPEG at the same visual quality, so
// large plans stop at a near-lossless encode instead of sliding down the
// ladder. Browsers that can't encode WebP (older Safari) hand back a PNG
// blob from toBlob, which simply fails the size check and falls through.
// Qualities below ~0.78 visibly smear the line work and text on site plans.
const LOSSY_ATTEMPTS = [
  ['image/webp', 0.9],
  ['image/jpeg', 0.92],
  ['image/webp', 0.8],
  ['image/jpeg', 0.85],
  ['image/jpeg', 0.78],
]

const extensionForType = (type) =>
  ({
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
  })[type] || 'jpg'

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new window.Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(
        new Error(
          'This image could not be read — it may be too large for the browser to open. Try exporting it at a smaller size.',
        ),
      )
    }
    image.src = url
  })

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality))

export async function prepareEventMapImage(file) {
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error(
      'That image is too large to process. Please export it at a smaller size (under 250MB) and try again.',
    )
  }

  const image = await loadImage(file)
  const width = image.naturalWidth
  const height = image.naturalHeight
  if (!width || !height) {
    throw new Error('This image could not be read.')
  }

  const scale = Math.min(
    1,
    MAX_EDGE / Math.max(width, height),
    Math.sqrt(MAX_PIXELS / (width * height)),
  )

  // Already within display limits and small enough to upload: keep the
  // original bytes (preserves lossless PNG line work).
  if (scale === 1 && file.size <= EVENT_MAP_MAX_UPLOAD_BYTES) {
    return {
      blob: file,
      contentType: file.type,
      extension: extensionForType(file.type),
      width,
      height,
    }
  }

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height)

  // PNG keeps site-plan line work crisp; fall back through the lossy ladder
  // when the PNG is still over the upload cap.
  const attempts =
    file.type === 'image/png'
      ? [['image/png'], ...LOSSY_ATTEMPTS]
      : LOSSY_ATTEMPTS

  for (const [type, quality] of attempts) {
    const blob = await canvasToBlob(canvas, type, quality)
    if (blob && blob.size <= EVENT_MAP_MAX_UPLOAD_BYTES) {
      return {
        blob,
        contentType: blob.type,
        extension: extensionForType(blob.type),
        width: canvas.width,
        height: canvas.height,
      }
    }
  }

  throw new Error(
    'This image could not be compressed enough to upload. Please export it at a smaller size and try again.',
  )
}
